import { useMemo } from 'react';
import type { DraftState, TeamLiveData } from '../types';
import { TEAMS_BY_ABBR } from '../constants/teams';
import { TeamLogo } from './TeamLogo';

interface Props {
  state: DraftState;
  liveData: Record<string, TeamLiveData>;
  rankings: Record<string, number>;
}

interface ManagerStats {
  id: string;
  name: string;
  emoji?: string;
  teams: string[];
  spent: number;
  totalProjWins: number;
  /** Average ESPN FPI of drafted teams (positive = above replacement, negative = below) */
  avgFpi: number | null;
  /** Average power ranking of drafted teams (lower = better) */
  avgRank: number | null;
  /** $ spent per projected win — lower is more efficient */
  dollarsPerProjWin: number | null;
  /** Live in-season record */
  wins: number;
  losses: number;
}

/**
 * Live "Power Rankings of the Managers" — ranks each league member by the
 * total projected wins of their drafted roster, with FPI as a tiebreaker.
 * Shows efficiency ($ per projected win) and in-season W-L sum too.
 */
export function PortfolioLeaderboard({ state, liveData, rankings }: Props) {
  const stats: ManagerStats[] = useMemo(() => {
    return state.managers.map(m => {
      const teams = m.teams;
      let totalProjWins = 0;
      let projWinsCount = 0;
      let fpiSum = 0;
      let fpiCount = 0;
      let rankSum = 0;
      let rankCount = 0;
      let wins = 0;
      let losses = 0;
      teams.forEach(abbr => {
        const d = liveData[abbr];
        if (d?.projectedWins != null) { totalProjWins += d.projectedWins; projWinsCount++; }
        if (d?.fpi != null) { fpiSum += d.fpi; fpiCount++; }
        if (rankings[abbr] != null) { rankSum += rankings[abbr]; rankCount++; }
        if (d?.record) { wins += d.record.wins; losses += d.record.losses; }
      });
      const spent = m.initialBudget - m.budget;
      return {
        id: m.id,
        name: m.name,
        emoji: m.emoji,
        teams,
        spent,
        totalProjWins: projWinsCount > 0 ? totalProjWins : 0,
        avgFpi: fpiCount > 0 ? fpiSum / fpiCount : null,
        avgRank: rankCount > 0 ? rankSum / rankCount : null,
        dollarsPerProjWin: projWinsCount > 0 && totalProjWins > 0 ? spent / totalProjWins : null,
        wins,
        losses,
      };
    });
  }, [state.managers, liveData, rankings]);

  const ranked = useMemo(() => {
    return [...stats].sort((a, b) => {
      if (b.totalProjWins !== a.totalProjWins) return b.totalProjWins - a.totalProjWins;
      const af = a.avgFpi ?? -999, bf = b.avgFpi ?? -999;
      if (bf !== af) return bf - af;
      return (a.avgRank ?? 33) - (b.avgRank ?? 33);
    });
  }, [stats]);

  const anyDrafted = ranked.some(r => r.teams.length > 0);
  if (!anyDrafted) return null;

  const maxProj = Math.max(1, ...ranked.map(r => r.totalProjWins));

  const medals = ['🥇', '🥈', '🥉', '🏈'];

  return (
    <div className="panel p-4">
      <div className="flex items-end justify-between mb-3">
        <div>
          <h2 className="text-lg font-extrabold text-white">🏆 Portfolio Leaderboard</h2>
          <p className="text-[10px] text-slate-500">Ranked by total projected wins of drafted rosters · FPI is tiebreaker.</p>
        </div>
      </div>

      <ol className="space-y-2">
        {ranked.map((r, i) => {
          const pct = (r.totalProjWins / maxProj) * 100;
          const isLeader = i === 0 && r.teams.length > 0;
          return (
            <li
              key={r.id}
              className={`relative rounded-lg p-3 border ${
                isLeader ? 'bg-amber-900/20 border-amber-500/60' : 'bg-slate-800/40 border-slate-700'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl w-8 text-center">{medals[i] ?? '🏈'}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{r.emoji}</span>
                    <span className="font-extrabold text-white">{r.name}</span>
                    <span className="text-[10px] text-slate-500">· {r.teams.length} team{r.teams.length === 1 ? '' : 's'}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1 flex flex-wrap gap-2">
                    <span><span className="text-amber-300 font-bold">{r.totalProjWins.toFixed(1)}</span> proj W</span>
                    {r.avgFpi != null && (
                      <span>
                        avg FPI{' '}
                        <span className={r.avgFpi >= 0 ? 'text-emerald-300 font-bold' : 'text-rose-300 font-bold'}>
                          {r.avgFpi >= 0 ? '+' : ''}{r.avgFpi.toFixed(1)}
                        </span>
                      </span>
                    )}
                    {r.avgRank != null && (
                      <span>avg rank <span className="text-teal-300 font-bold">#{r.avgRank.toFixed(1)}</span></span>
                    )}
                    {r.dollarsPerProjWin != null && (
                      <span title="$ spent per projected win — lower = better value">
                        <span className="text-emerald-300 font-bold">${r.dollarsPerProjWin.toFixed(2)}</span>/proj W
                      </span>
                    )}
                    {(r.wins + r.losses) > 0 && (
                      <span>live record <span className="text-white font-bold">{r.wins}-{r.losses}</span></span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 max-w-[40%] justify-end">
                  {r.teams.slice(0, 8).map(abbr => (
                    <TeamLogo
                      key={abbr}
                      abbr={abbr}
                      size={20}
                      className="opacity-90"
                      title={`${TEAMS_BY_ABBR[abbr]?.city ?? abbr} ${TEAMS_BY_ABBR[abbr]?.name ?? ''}`}
                    />
                  ))}
                </div>
              </div>
              {/* Projected wins bar */}
              <div className="mt-2 h-1.5 bg-slate-900 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    isLeader ? 'bg-gradient-to-r from-amber-500 to-amber-300' : 'bg-teal-500'
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
